import { createClient } from '@supabase/supabase-js'

const supabaseUrl     = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('[Rectorium] Variables VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY manquantes dans .env')
}

/* ═══════════════════════════════════════════════════════════════════════════
   TENANT STORE — singleton module-level, déclaré AVANT le client
   pour que le fetch-override le capture par référence
   ═══════════════════════════════════════════════════════════════════════════ */
let _tenantId  = null
let _tenantObj = null

export const setActiveTenant = (tenant) => {
  _tenantId  = tenant?.id  ?? null
  _tenantObj = tenant ?? null
}

export const getActiveTenantId = () => _tenantId
export const getActiveTenant   = () => _tenantObj

/* ═══════════════════════════════════════════════════════════════════════════
   CLIENT SUPABASE — avec fetch override pour injecter x-tenant-id
   sur chaque requête PostgREST → RLS côté DB peut lire l'header
   ═══════════════════════════════════════════════════════════════════════════ */
const _client = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true },
  global: {
    fetch: (url, options = {}) => {
      // options.headers is a Headers instance in postgrest-js v2 — spread gives {}
      // Use new Headers() to safely copy all entries (auth, apikey, etc.)
      const h = new Headers(options.headers || {})
      // Filet de secours (signalé par Alfred, 2026-10-08, capture DevTools :
      // "No apikey found in request", HTTP 400 sur /rest/v1/profiles) — dans
      // certains enchaînements (navigation rapide, bascule d'identité),
      // postgrest-js construit parfois la requête sans apikey dans
      // `options.headers` avant que ce wrapper ne s'exécute. `apikey` est
      // une constante publique du projet (jamais secrète côté client) : la
      // reposer ici si absente ne peut jamais rien casser, seulement éviter
      // ce 400.
      if (!h.has('apikey')) h.set('apikey', supabaseAnonKey)
      h.set('x-app-name', 'Rectorium-GouvAcad')
      // x-tenant-id : conserve pour le filtrage applicatif du proxy ci-dessous.
      // Il n'etablit AUCUNE autorite cote serveur (les policies derivent le
      // tenant du profil de auth.uid()).
      if (_tenantId) h.set('x-tenant-id', String(_tenantId))

      // En-tetes d'IDENTITE retires (chantier « identite unifiee », 2026-07-27) :
      // plus de x-pin-session (la connexion par PIN n'existe plus), plus de
      // x-user-id/x-user-role legacy. L'identite vient EXCLUSIVEMENT du JWT
      // Supabase Auth, lu cote serveur par auth.uid() → get_request_user_id()
      // → resolve_session(). Un en-tete client ne peut plus rien affirmer.
      return fetch(url, { ...options, headers: h })
    },
  },
})

/* ═══════════════════════════════════════════════════════════════════════════
   TABLES MÉTIER portant une colonne tenant_id
   ═══════════════════════════════════════════════════════════════════════════ */
// Noms exacts des tables DB (vérifiés via Supabase)
const TENANT_TABLES = new Set([
  // Structure académique
  'schools', 'campuses', 'specialties',
  'academic_years', 'semesters', 'academic_levels', 'classes',
  'academic_units', 'course_subjects', 'schedules',
  // Personnes
  'profiles', 'director_schools', 'system_permissions',
  // Résultats & présences
  'attendance', 'academic_results',
  'student_enrollments', 'teacher_assignments', 'teacher_sessions',
  // Délibérations & requêtes
  'deliberations', 'academic_requests', 'student_followups', 'grade_change_requests',
  // Finances
  'student_payments', 'budget_lines', 'expenditure_requests', 'tenant_approval_steps',
  // Stages & RH
  'internships', 'internship_offers', 'job_positions', 'companies', 'alumni_profiles',
  // Communication & forums
  'conversations', 'conversation_members', 'messages', 'message_reads', 'student_messages',
  'forums', 'forum_topics', 'forum_messages', 'forum_replies',
  // Contenu & documents
  'formation_content',
  // LMS complet
  'lms_courses', 'lms_sections', 'lms_materials',
  'lms_assignments', 'lms_quizzes',
  'lms_progress', 'lms_quiz_attempts', 'lms_quiz_questions', 'lms_submissions',
  // Virtual
  'virtual_sessions',
  // Audit & divers
  'audit_logs', 'public_events',
  // Bibliothèque Numérique
  'library_documents', 'library_collections', 'library_favorites', 'library_downloads', 'library_reviews',
  // Tenant management → EXCLUS (pas d'isolation tenant_id)
])

/* ═══════════════════════════════════════════════════════════════════════════
   PROXY APPLICATION — injecte tenant_id dans toutes les requêtes
   SELECT / INSERT / UPDATE / DELETE / UPSERT
   (double protection : application + DB via RLS)
   ═══════════════════════════════════════════════════════════════════════════ */
const withTenantFilter = (builder, table) => {
  const tid = _tenantId
  if (!tid || !TENANT_TABLES.has(table)) return builder

  return new Proxy(builder, {
    get (target, prop, receiver) {

      /* ── SELECT ── */
      if (prop === 'select') {
        return (...args) => target.select(...args).eq('tenant_id', tid)
      }

      /* ── INSERT ── */
      if (prop === 'insert') {
        return (data, opts) => {
          const tagged = Array.isArray(data)
            ? data.map(d => ({ tenant_id: tid, ...d }))
            : { tenant_id: tid, ...data }
          return target.insert(tagged, opts)
        }
      }

      /* ── UPDATE ── */
      if (prop === 'update') {
        return (data, opts) => target.update(data, opts).eq('tenant_id', tid)
      }

      /* ── DELETE ── */
      if (prop === 'delete') {
        return (opts) => target.delete(opts).eq('tenant_id', tid)
      }

      /* ── UPSERT ── */
      if (prop === 'upsert') {
        return (data, opts) => {
          const tagged = Array.isArray(data)
            ? data.map(d => ({ tenant_id: tid, ...d }))
            : { tenant_id: tid, ...data }
          return target.upsert(tagged, opts)
        }
      }

      return Reflect.get(target, prop, receiver)
    },
  })
}

/* Proxy sur le client principal */
export const supabase = new Proxy(_client, {
  get (target, prop, receiver) {
    if (prop === 'from') {
      return (table) => withTenantFilter(target.from(table), table)
    }
    return Reflect.get(target, prop, receiver)
  },
})

/* ═══════════════════════════════════════════════════════════════════════════
   CANAL IDENTITÉ — la ligne de SOI dans `profiles`, sans cloisonnement tenant.

   La ligne du Master porte tenant_id NULL : pendant une inspection, le proxy
   ci-dessus ajouterait `tenant_id=eq.<tenant inspecté>` et la rendrait
   inatteignable depuis son propre écran de profil — sauvegarde en 406
   « Cannot coerce », avatar en « 204 » à zéro ligne, toast de succès menteur
   (GRP 42.8, 2026-08-21). Réservé à la ligne `id = auth.uid()` : la barrière
   RÉELLE reste la RLS serveur (`profiles_update_own_or_admin` n'autorise en
   écriture que sa propre ligne) — ce canal ne peut donc rien atteindre de
   plus, il cesse seulement de s'interdire sa propre identité.
   ═══════════════════════════════════════════════════════════════════════════ */
export const monProfil = () => _client.from('profiles')

/* ═══════════════════════════════════════════════════════════════════════════
   QUOTA CHECK — vérifie les limites de licence avant INSERT
   Usage : const q = await checkTenantQuota('students');
           if (!q.ok) { alert(`Quota dépassé (${q.count}/${q.limit})`); return; }
   ═══════════════════════════════════════════════════════════════════════════ */
export const checkTenantQuota = async (resource) => {
  const tenant = getActiveTenant()
  if (!tenant) return { ok: true }

  const cfgMap = {
    students: { table: 'profiles', eq: ['role', 'Apprenant'],   limit: tenant.max_students  },
    teachers: { table: 'profiles', eq: ['role', 'Enseignant'],  limit: tenant.max_teachers  },
    campuses: { table: 'campuses', eq: null,                    limit: tenant.max_campuses  },
    schools:  { table: 'schools',  eq: null,                    limit: tenant.max_schools   },
    users:    { table: 'profiles', eq: null,                    limit: tenant.max_users     },
  }

  const cfg = cfgMap[resource]
  if (!cfg || !cfg.limit) return { ok: true }

  let q = _client.from(cfg.table).select('*', { count: 'exact', head: true })
  if (cfg.eq) q = q.eq(cfg.eq[0], cfg.eq[1])

  // LE REPLI SUR ZÉRO OUVRAIT LE QUOTA (corrigé le 2026-09-12, critère C6).
  // `const current = count || 0` transformait toute lecture indisponible en
  // « rien de consommé », donc en « quota disponible » : la garde ACCORDAIT à
  // chaque fois qu'elle ne pouvait pas compter. `supabase-js` ne lève pas —
  // l'erreur n'était même pas lue. Une garde dont l'échec autorise n'est pas
  // une garde ; elle se referme désormais, et dit pourquoi.
  const { count, error } = await q
  if (error || count === null || count === undefined) {
    console.error(`Quota « ${resource} » — comptage indisponible :`, error)
    return { ok: false, count: null, limit: cfg.limit, indetermine: true }
  }
  return { ok: count < cfg.limit, count, limit: cfg.limit }
}
